import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart3, CheckCircle2, XCircle, Loader2, Lock, Eye, EyeOff, Check, X, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RULES = [
  { key: 'len', label: 'At least 10 characters', test: (p) => p.length >= 10 },
  { key: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'digit', label: 'A number', test: (p) => /[0-9]/.test(p) },
  { key: 'symbol', label: 'A symbol (!@#$…)', test: (p) => /[^A-Za-z0-9]/.test(p) },
];
const STRENGTH = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very strong'];
const STRENGTH_COLOR = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e'];

// Inline "request a new reset link" box — reused for missing/expired links.
function RequestNewLink({ onBack }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (_) { /* generic response regardless */ }
    setBusy(false);
    setSent(true);
  };
  if (sent) {
    return (
      <div className="text-center" data-testid="reset-link-sent">
        <Mail className="w-12 h-12 text-blue-400 mx-auto mb-3" />
        <h2 className="text-xl font-heading font-semibold text-white mb-2">Check your inbox</h2>
        <p className="text-sm text-zinc-400 mb-6 leading-relaxed">If an account exists for that email, we&apos;ve sent a fresh reset link. It expires in 1 hour.</p>
        <Button onClick={onBack} variant="outline" className="rounded-xl bg-white/5 border-white/10 text-zinc-300" data-testid="reset-back-to-login">Back to Sign In</Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-request-new">
      <div className="space-y-1.5">
        <Label className="text-zinc-300 text-xs">Your email</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="reset-request-email" type="email" required placeholder="you@company.com" value={email}
            onChange={e => setEmail(e.target.value)}
            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl" />
        </div>
      </div>
      <Button type="submit" disabled={busy} className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white" data-testid="reset-request-submit">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Sending…</> : 'Send a new reset link'}
      </Button>
      <Button type="button" onClick={onBack} variant="ghost" className="w-full rounded-xl text-zinc-400 hover:text-zinc-200">Back to Sign In</Button>
    </form>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [linkDead, setLinkDead] = useState(false); // token invalid/expired → offer new link

  const passed = RULES.filter(r => r.test(password));
  const score = passed.length;
  const allMet = score === RULES.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allMet) { setError('Your password doesn\'t meet all the requirements yet.'); return; }
    if (password !== confirm) { setError('The two passwords don\'t match.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = (data.detail || data.message || '').toString();
        if (/expired|invalid/i.test(msg)) { setLinkDead(true); return; }
        throw new Error(msg || 'Could not reset your password.');
      }
      setDone(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message || 'Could not reset your password.');
    } finally {
      setBusy(false);
    }
  };

  const showRequestBox = !token || linkDead;

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-12" style={{ background: 'radial-gradient(ellipse at top, rgba(59, 130, 246, 0.06) 0%, rgba(0, 0, 0, 0) 60%), #0a0a0b' }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="p-2 rounded-xl bg-blue-500/10 border border-blue-500/20"><BarChart3 className="w-6 h-6 text-blue-400" strokeWidth={1.5} /></div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">INZARA</h1>
        </div>

        <div className="rounded-2xl p-8" style={{ background: 'rgba(255, 255, 255, 0.02)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <h2 className="text-xl font-heading font-semibold text-white mb-2">Password updated</h2>
              <p className="text-sm text-zinc-400 mb-6">You can now sign in with your new password — taking you there…</p>
              <Loader2 className="w-5 h-5 text-zinc-500 mx-auto animate-spin" />
            </div>
          ) : showRequestBox ? (
            <div data-testid="reset-link-dead">
              <div className="text-center mb-6">
                <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <h2 className="text-xl font-heading font-semibold text-white mb-2">{linkDead ? 'This link has expired' : 'Invalid reset link'}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">{linkDead ? 'Reset links are valid for 1 hour and can be used once. Request a fresh one below.' : 'This link is missing its token. Request a fresh one below.'}</p>
              </div>
              <RequestNewLink onBack={() => navigate('/login')} />
            </div>
          ) : (
            <>
              <h2 className="text-xl font-heading font-semibold text-white mb-1 text-center">Set a new password</h2>
              <p className="text-sm text-zinc-400 mb-6 text-center">Choose a strong password you haven&apos;t used before.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs">New password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input data-testid="reset-password-input" type={show ? 'text' : 'password'} placeholder="Create a strong password"
                      value={password} onChange={e => setPassword(e.target.value)}
                      className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl" required />
                    <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300" data-testid="reset-toggle-visibility">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Strength bar */}
                  {password && (
                    <div className="pt-1" data-testid="reset-strength">
                      <div className="flex gap-1 mb-1.5">
                        {RULES.map((_, i) => (
                          <div key={i} className="h-1 flex-1 rounded-full transition-colors" style={{ background: i < score ? STRENGTH_COLOR[score] : 'rgba(255,255,255,0.08)' }} />
                        ))}
                      </div>
                      <p className="text-[11px] font-medium" style={{ color: STRENGTH_COLOR[score] }} data-testid="reset-strength-label">{STRENGTH[score]}</p>
                    </div>
                  )}

                  {/* Rules checklist */}
                  <ul className="grid grid-cols-1 gap-1 pt-1" data-testid="reset-rules">
                    {RULES.map(r => {
                      const ok = r.test(password);
                      return (
                        <li key={r.key} className="flex items-center gap-2 text-[11px]" style={{ color: ok ? '#22c55e' : '#71717a' }} data-testid={`reset-rule-${r.key}-${ok ? 'ok' : 'no'}`}>
                          {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {r.label}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-zinc-300 text-xs">Confirm password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input data-testid="reset-confirm-input" type={show ? 'text' : 'password'} placeholder="Re-enter your new password"
                      value={confirm} onChange={e => setConfirm(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500/50 rounded-xl" required />
                  </div>
                  {confirm && confirm !== password && <p className="text-[11px] text-amber-400">Passwords don&apos;t match yet.</p>}
                </div>

                {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}

                <Button data-testid="reset-submit-btn" type="submit" disabled={busy || !allMet || password !== confirm} className="w-full rounded-xl bg-blue-500 hover:bg-blue-600 text-white">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Updating…</> : 'Reset password'}
                </Button>
                <Button type="button" onClick={() => navigate('/login')} variant="ghost" className="w-full rounded-xl text-zinc-400 hover:text-zinc-200" data-testid="reset-cancel">Back to Sign In</Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
